/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useRef } from 'react'
import type { ReactNode } from 'react'
import type { StyleProp, FlexProps } from '@vtex/admin-ui'
import { csx, cx } from '@vtex/admin-ui'

import type { DragData } from '../../shared/types'
import { useDropWithHighlights } from '../../hooks/useDropWithHighlights'
import { InStoreListArrow } from './InStoreListArrow'

const headingStyles: StyleProp = {
  fontSize: '1.5rem',
  paddingY: '$space-5',
  textAlign: 'center',
  text: '$pageTitle',
}

const listStyles: StyleProp = {
  overflowY: 'scroll',
  backgroundColor: '$secondary',
  borderRadius: '4px',
  flexGrow: 1,
}

type InStoreListProps<DragItem> = {
  onItemDrop: (dragItem: DragData<DragItem>) => void
  title: string
  dndAccept: string | string[]
  children: ReactNode
  showUpNavigation: boolean
  showDownNavigation: boolean
  'data-testid': string
} & FlexProps

// eslint-disable-next-line vtex/consistent-props-type
export const InStoreList = <DragItem,>({
  onItemDrop,
  title,
  dndAccept,
  showUpNavigation,
  showDownNavigation,
  children,
  className = '',
  ...props
}: InStoreListProps<DragItem>) => {
  const itemsContainerRef = useRef<HTMLDivElement>(null)

  const [highlightStyles, drop] = useDropWithHighlights<
    DragData<DragItem>,
    unknown,
    unknown
  >({
    accept: dndAccept,
    drop: (item) => onItemDrop(item),
  })

  return (
    <div
      ref={drop}
      className={cx(
        csx({
          overflowY: 'hidden',
          boxSizing: 'border-box',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }),
        className
      )}
      {...props}
    >
      <p className={csx({ ...headingStyles })}>{title}</p>
      <div
        ref={itemsContainerRef}
        className={csx({
          ...listStyles,
          ...highlightStyles,
          borderBottom: 'none',
        })}
        data-testid={`${props['data-testid']}-items-container`}
      >
        <InStoreListArrow
          itemsContainerRef={itemsContainerRef}
          show={showUpNavigation}
          direction="up"
        />
        <div
          className={csx({
            display: 'flex',
            paddingY: '$space-2',
            paddingX: '$space-2',
            gap: '$space-2',
            flexDirection: 'column',
          })}
        >
          {children}
        </div>
        <InStoreListArrow
          itemsContainerRef={itemsContainerRef}
          show={showDownNavigation}
          direction="down"
        />
      </div>
    </div>
  )
}
